import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { cn } from '#/lib/utils';
import { lookupQueryKey } from '#/utils/queryKeys';

/**
 * 可編主檔：預交日、事由／備註。
 * 預交日單獨一列（半寬靠左）；事由／備註 @xl 同列兩欄。
 */
const SalesOrderMainFieldsSection = ({
  control,
  register,
  errors,
  currencyLocked = false,
  savedCurrency = null,
}) => {
  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
    enabled: !currencyLocked,
  });

  const currencyOptions = useMemo(() => {
    const list = currencyOptionsQuery.data ?? [];
    if (!savedCurrency?.id || list.some((item) => item.value === savedCurrency.id)) {
      return list;
    }
    const label = savedCurrency.code || savedCurrency.id;
    return [...list, { value: savedCurrency.id, label }];
  }, [currencyOptionsQuery.data, savedCurrency]);

  return (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">預交與備註</h2>
      </div>
      <div className="flex flex-col gap-3">
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            '@xl/sheet:grid-cols-2',
          )}
        >
          <FloatingDateInput
            id="plannedShipDate"
            label="預交日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
            numberOfMonths={2}
          />
          {currencyLocked ? null : (
            <FloatingSelect
              id="currencyId"
              label="幣別"
              options={currencyOptions}
              control={control}
              errors={errors}
              isDisabled={currencyOptionsQuery.isLoading}
              noOptionsMessage="無可用幣別"
            />
          )}
        </div>
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            '@xl/sheet:grid-cols-2',
          )}
        >
          <FloatingTextarea
            id="issueReason"
            label="事由"
            register={register}
            errors={errors}
          />
          <FloatingTextarea
            id="remark"
            label="備註"
            register={register}
            errors={errors}
          />
        </div>
      </div>
    </section>
  );
};

SalesOrderMainFieldsSection.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  currencyLocked: PropTypes.bool,
  savedCurrency: PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
  }),
};

export default SalesOrderMainFieldsSection;
